/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { ImageWithFallback } from '@mpb-com/feather/components/atoms/image-with-fallback';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent, useRef } from 'react';
import styled from 'styled-components';

import {
    ProductConditionValue,
    ProductState,
    fallbackImagePath,
} from '@toucan/client/global/product';

import { scrollToElement } from '@toucan/client/core/scroll';

import {
    ConstrainedWrapper,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { FormattedCondition } from '@toucan/client/shared/components/formatted-condition';
import { PaginationVariant } from '@toucan/client/shared/components/pagination';
import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';

import { Breadcrumb } from '@toucan/client/blocks/components/breadcrumb';
import {
    Carousel,
    CarouselVariant,
} from '@toucan/client/blocks/components/carousel';
import { ProductAddToBasket } from '@toucan/client/blocks/components/product-add-to-basket';

import {
    INSIGHTS_ACTION_EVENT,
    InsightsAction,
    KitIntelligenceSection,
    ModelJourneyFan,
    ModelOverview,
    modelToModelOverviewProps,
} from '@toucan/client/features/model';
import { ModelRecommendationsCarousel } from '@toucan/client/features/search';
import { ProductTrustBlock } from '@toucan/client/features/trust';

import { ProductDetails } from '@toucan/client/views/product/components/product-details';

import { ProductAddToQuoteLink } from './components/product-add-to-quote-link';
import { ProductDetailSidebar } from './components/product-detail-sidebar';
import { ProductOutOfStock } from './components/product-out-of-stock';
import { ProductPrice } from './components/product-price';
import { DEFAULT_TEST_ID } from './constants';
import { styles } from './product.styles';
import { ProductProps } from './types';

const StyledProduct = styled.div`
    ${styles}
`;

export const Product: FunctionComponent<ProductProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    productInfo,
    pageData,
    stockInfo,
    breadcrumbs,
    parentCategory,
    modelInfo,
    modelRecommendations,
    modelPurchaseJourney,
    kitIntelligence,
    otherStock,
    refreshProductData,
    ...otherProps
}: ProductProps) => {
    const { t } = useTranslation();

    const insightsRef = useRef<HTMLDivElement>(null);

    const hasInsights =
        Boolean(modelPurchaseJourney) || Boolean(kitIntelligence);

    const isNewProduct = productInfo.condition === ProductConditionValue.NEW;
    const isOutOfStock = productInfo.state !== ProductState.ONLINE;

    const modelOverViewProps = modelToModelOverviewProps(
        t,
        modelInfo,
        isNewProduct
    );

    const imageNotice = !isNewProduct
        ? t(
              "We always photograph each unique item, so this is the exact product you'll receive."
          )
        : '';

    const handleInsightsActionClick = () => {
        scrollToElement(insightsRef.current);

        trackViaDataLayer({
            event: INSIGHTS_ACTION_EVENT,
            model_id: modelInfo.id,
        });
    };

    return (
        <StyledProduct {...otherProps} data-testid={dataTestId}>
            <ConstrainedWrapper
                className="constrained-wrapper"
                type={ConstrainedWrapperType.PADDING}
            >
                <Breadcrumb
                    links={breadcrumbs}
                    className="breadcrumb"
                    data-testid={`${dataTestId}__breadcrumb`}
                />
                <div
                    className="product-images-carousel-container"
                    data-testid={`${dataTestId}__product-carousel`}
                >
                    <Carousel
                        showExpandButton
                        variant={CarouselVariant.MINIMAL}
                        paginationVariant={PaginationVariant.THUMBNAIL}
                        maxSlideHeight="50vh"
                        note={imageNotice}
                        items={productInfo.images.map(
                            ({ alt, imageUrl }, index) => (
                                <ImageWithFallback
                                    // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                                    key={index}
                                    alt={alt}
                                    src={imageUrl}
                                    fallbackSrc={fallbackImagePath}
                                />
                            )
                        )}
                        paginationItems={productInfo.images.map(
                            ({ alt, thumb }) => ({
                                image: (
                                    <ImageWithFallback
                                        alt={alt}
                                        src={thumb}
                                        fallbackSrc={fallbackImagePath}
                                    />
                                ),
                            })
                        )}
                    />
                </div>
                <div className="product-details-container">
                    <div className="product-name-row">
                        <Heading
                            className="product-name"
                            data-testid={`${dataTestId}__heading`}
                            size={HeadingSize.H2}
                            as="h1"
                        >
                            <strong>{productInfo.name}</strong>
                        </Heading>
                        {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                        {hasInsights && (
                            <InsightsAction
                                data-testid={`${dataTestId}__insights-action`}
                                onClick={handleInsightsActionClick}
                            />
                        )}
                    </div>
                    <FormattedCondition
                        className="product-condition"
                        data-testid={`${dataTestId}__product-condition`}
                        condition={productInfo.condition}
                        boldConditionValue={false}
                        conditionDescription={productInfo.conditionDescription}
                    />
                    <ProductPrice
                        className="product-price"
                        listPrice={parseFloat(productInfo.listPrice)}
                        originalPrice={parseFloat(
                            productInfo.productPriceOriginal
                        )}
                    />
                    <div className="product-actions">
                        {isOutOfStock ? (
                            <ProductOutOfStock
                                className="product-out-of-stock"
                                productData={productInfo}
                                data-testid={`${dataTestId}__product-out-of-stock`}
                                stockInfo={stockInfo}
                                parentCategory={parentCategory}
                            />
                        ) : (
                            <Fragment>
                                <ProductAddToBasket
                                    className="product-add-to-basket"
                                    productData={productInfo}
                                />
                                <ProductAddToQuoteLink
                                    productData={productInfo}
                                    refreshProductData={refreshProductData}
                                />
                            </Fragment>
                        )}
                    </div>
                    <ProductDetails productInfo={productInfo} />
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {pageData && !isNewProduct && (
                        <ProductDetailSidebar
                            product={productInfo}
                            pageData={pageData}
                            className="product-detail-sidebar"
                            data-testid={`${dataTestId}__product-detail-sidebar`}
                        />
                    )}
                </div>
            </ConstrainedWrapper>
            <div ref={insightsRef} className="product-insights">
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {modelPurchaseJourney && (
                    <ModelJourneyFan
                        className="model-journey"
                        journey={modelPurchaseJourney}
                        modelName={modelInfo.name}
                    />
                )}
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {kitIntelligence && (
                    <KitIntelligenceSection
                        className="kit-intelligence"
                        kitIntelligence={kitIntelligence}
                        modelName={modelInfo.name}
                    />
                )}
            </div>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {modelOverViewProps && (
                <ModelOverview
                    {...modelOverViewProps}
                    otherStock={otherStock}
                    className="model-overview"
                    data-testid={`${dataTestId}__model-overview`}
                />
            )}
            <ModelRecommendationsCarousel
                data-testid={`${dataTestId}__model-recommendations-carousel`}
                pageModel={modelInfo}
                pageProduct={productInfo}
                modelRecommendations={modelRecommendations}
            />
            <ProductTrustBlock data-testid={`${dataTestId}__trust-block`} />
        </StyledProduct>
    );
};
